import axios from "axios";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

export const api = {
  config: () => client.get(`/config`).then((r) => r.data),
  me: () => client.get(`/auth/me`).then((r) => r.data),
  register: (body) => client.post(`/auth/register`, body).then((r) => r.data),
  login: (body) => client.post(`/auth/login`, body).then((r) => r.data),
  logout: () => client.post(`/auth/logout`).then((r) => r.data),
  googleSession: (session_id, role) => client.post(`/auth/session`, { session_id, role }).then((r) => r.data),
  forgotPassword: (email) => client.post(`/auth/forgot-password`, { email }).then((r) => r.data),
  resetPassword: (token, password) => client.post(`/auth/reset-password`, { token, password }).then((r) => r.data),
  verifyEmail: (token) => client.post(`/auth/verify-email`, { token }).then((r) => r.data),
  resendVerification: () => client.post(`/auth/resend-verification`).then((r) => r.data),
  accountJobs: () => client.get(`/account/jobs`).then((r) => r.data),
  accountTester: () => client.get(`/account/tester`).then((r) => r.data),
  terms: (which) => client.get(`/terms/${which}`).then((r) => r.data),
  checkout: (body) => client.post(`/checkout`, body).then((r) => r.data),
  creditBalance: (email, cur) => client.get(`/credits/balance`, { params: { email, cur } }).then((r) => r.data),
  track: (body) => client.post(`/track`, body).then((r) => r.data).catch(() => null),
  paymentStatus: (sid) => client.get(`/payments/status/${sid}`).then((r) => r.data),
  status: (token) => client.get(`/status/${token}`).then((r) => r.data),
  rerun: (token, body) => client.post(`/rerun/${token}`, body).then((r) => r.data),
  historyRequest: (email) => client.post(`/client/history`, { email }).then((r) => r.data),
  history: (token) => client.get(`/client/history/${token}`).then((r) => r.data),
  intake: (token, body) => client.post(`/intake/${token}`, body).then((r) => r.data),
  uploadIntakeFile: async (token, file, onProgress) => {
    const CHUNK = 3 * 1024 * 1024;
    const uploadId = Math.random().toString(36).slice(2) + Date.now().toString(36);
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(i));
      fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
      await client.post(`/intake/${token}/file/chunk`, fd);
      onProgress?.(Math.round(((i + 1) / total) * 90));
    }
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("filename", file.name);
    fd.append("content_type", file.type || "application/octet-stream");
    fd.append("total", String(total));
    const r = await client.post(`/intake/${token}/file/complete`, fd);
    onProgress?.(100);
    return r.data;
  },
  deleteIntakeFile: (token, fid) => client.delete(`/intake/${token}/file/${fid}`).then((r) => r.data),
  session: (token) => client.get(`/session/${token}`).then((r) => r.data),
  sessionAccept: (token) => client.post(`/session/${token}/accept`).then((r) => r.data),
  sessionStart: (token, body) => client.post(`/session/${token}/start`, body).then((r) => r.data),
  sessionCase: (token, body) => client.post(`/session/${token}/case`, body).then((r) => r.data),
  sessionFinding: (token, body) => client.post(`/session/${token}/finding`, body).then((r) => r.data),
  sessionSubmit: (token) => client.post(`/session/${token}/submit`).then((r) => r.data),
  join: (body) => client.post(`/join`, body).then((r) => r.data),
  unsubscribe: (id) => client.post(`/unsubscribe/${id}`).then((r) => r.data),
  uploadMedia: async (token, file, onProgress) => {
    const CHUNK = 4 * 1024 * 1024;
    const uploadId = Math.random().toString(36).slice(2) + Date.now().toString(36);
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(i));
      fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
      await client.post(`/session/${token}/media/chunk`, fd);
      onProgress?.(Math.round(((i + 1) / total) * 90));
    }
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("filename", file.name);
    fd.append("content_type", file.type || "application/octet-stream");
    fd.append("total", String(total));
    const r = await client.post(`/session/${token}/media/complete`, fd);
    onProgress?.(100);
    return r.data;
  },
  activateGet: (token) => client.get(`/activate/${token}`).then((r) => r.data),
  activatePost: (token, body) => client.post(`/activate/${token}`, body).then((r) => r.data),
  adminLogin: (body) => client.post(`/admin/login`, body).then((r) => r.data),
  deviceCounts: () => client.get(`/devices/counts`).then((r) => r.data).catch(() => ({ counts: {} })),
};

export function adminClient(token) {
  const c = axios.create({ baseURL: API, headers: { "X-Admin-Token": token } });
  return {
    overview: () => c.get(`/admin/overview`).then((r) => r.data),
    jobs: () => c.get(`/admin/jobs`).then((r) => r.data),
    releaseQueue: () => c.get(`/admin/release-queue`).then((r) => r.data),
    release: (job_id) => c.post(`/admin/release`, { job_id }).then((r) => r.data),
    appeal: (body) => c.post(`/admin/appeal`, body).then((r) => r.data),
    redispatch: (job_id, widen) => c.post(`/admin/redispatch`, { job_id, widen }).then((r) => r.data),
    refund: (job_id) => c.post(`/admin/refund`, { job_id }).then((r) => r.data),
    testers: (params = {}) => c.get(`/admin/testers`, { params }).then((r) => r.data),
    testerDetail: (id) => c.get(`/admin/testers/${id}`).then((r) => r.data),
    testerNote: (id, text) => c.post(`/admin/testers/${id}/notes`, { text }).then((r) => r.data),
    testerFlags: (id, flags) => c.put(`/admin/testers/${id}/flags`, { flags }).then((r) => r.data),
    jobDetail: (id) => c.get(`/admin/jobs/${id}`).then((r) => r.data),
    jobRedispatch: (id) => c.post(`/admin/jobs/${id}/redispatch`).then((r) => r.data),
    jobCredit: (id) => c.post(`/admin/refund`, { job_id: id }).then((r) => r.data),
    credits: () => c.get(`/admin/credits`).then((r) => r.data),
    creditAdjust: (body) => c.post(`/admin/credits/adjust`, body).then((r) => r.data),
    emails: (params = {}) => c.get(`/admin/emails`, { params }).then((r) => r.data),
    reactivationHistory: (days = 30) => c.get(`/admin/reactivation/history`, { params: { days } }).then((r) => r.data),
    payouts: () => c.get(`/admin/payouts`).then((r) => r.data),
    payoutLedger: () => c.get(`/admin/payouts/ledger`).then((r) => r.data),
    payoutMarkPaid: (body) => c.post(`/admin/payouts/mark-paid`, body).then((r) => r.data),
    payoutDigest: () => c.post(`/admin/payouts/digest`).then((r) => r.data),
    payoutExportUrl: (onlyDue) => `${API}/admin/payouts/export.csv?token=${encodeURIComponent(token)}&only_due=${onlyDue}`,
    intakeFileUrl: (fid) => `${API}/admin/intake-file/${fid}?token=${encodeURIComponent(token)}`,
    payoutRetry: (id) => c.post(`/admin/payouts/${id}/retry`).then((r) => r.data),
    events: (job_id) => c.get(`/admin/events`, { params: job_id ? { job_id } : {} }).then((r) => r.data),
    brain: () => c.get(`/admin/brain`).then((r) => r.data),
    brainFlip: (module, mode) => c.post(`/admin/brain/flip`, { module, mode }).then((r) => r.data),
    importCsv: (formData) => c.post(`/admin/testers/import`, formData).then((r) => r.data),
    reactivation: () => c.get(`/admin/reactivation`).then((r) => r.data),
    reactivationRun: (limit) => c.post(`/admin/reactivation/run`, { limit }).then((r) => r.data),
    emailTest: () => c.post(`/admin/emails/test`).then((r) => r.data),
    analytics: (days = 14) => c.get(`/admin/analytics`, { params: { days } }).then((r) => r.data),
    leads: () => c.get(`/admin/leads`).then((r) => r.data),
    leadsCsv: () => c.get(`/admin/leads.csv`, { responseType: "blob" }).then((r) => r.data),
    devicesCsv: () => c.get(`/admin/devices.csv`, { responseType: "blob" }).then((r) => r.data),
    leadsAb: () => c.get(`/admin/leads/ab`).then((r) => r.data),
  };
}

export function money(minor, currency) {
  const sym = currency === "USD" ? "$" : "₹";
  const major = minor / 100;
  return sym + major.toLocaleString(currency === "USD" ? "en-US" : "en-IN");
}
